import {
  WsmKitConfigCreateInput,
  WsmKitConfigDetailsFragment,
  WsmKitConfigUpdateInput,
  WsmKitDiscountKind,
  WsmKitMemberInput,
  WsmKitMemberRuleInput,
} from "@dashboard/graphql";
import { WsmAmountBasis } from "@dashboard/wsmShared/money";

import {
  WsmKitCreateFormData,
  WsmKitFormData,
  WsmKitMemberDraft,
  WsmKitRuleDraft,
} from "./types";

let draftKeySeq = 0;

/** A handle unique within one form, and so within the one mutation it submits. */
export const makeDraftKey = (prefix: string) => `${prefix}-${++draftKeySeq}`;

export const emptyKitFormData = (): WsmKitFormData => ({
  discountKind: WsmKitDiscountKind.FIXED,
  discountAmount: "0",
  freightClass: "",
  active: true,
  members: [],
  rules: [],
});

export const kitToFormData = (kit: WsmKitConfigDetailsFragment): WsmKitFormData => {
  const members: WsmKitMemberDraft[] = kit.members.map(member => ({
    key: makeDraftKey("member"),
    id: member.id,
    variantId: member.variant.id,
    variantName: member.variant.name,
    sku: member.variant.sku ?? "",
    productName: member.variant.product.name,
    quantity: member.quantity,
    sortOrder: member.sortOrder,
    label: member.label ?? "",
    labelTouched: false,
  }));
  const keyByMemberId = new Map(members.map(member => [member.id, member.key]));
  const rules: WsmKitRuleDraft[] = kit.rules.map(rule => ({
    key: makeDraftKey("rule"),
    id: rule.id,
    subjectKey: keyByMemberId.get(rule.subject.id) ?? "",
    kind: rule.kind,
    // A target the kit no longer has a member row for is dropped rather than
    // carried: re-submitting it is exactly what RULE_TARGET_NOT_IN_KIT refuses.
    targetKeys: rule.targets
      .map(target => keyByMemberId.get(target.id))
      .filter((key): key is string => !!key),
    message: rule.message,
  }));

  return {
    discountKind: kit.discountKind,
    discountAmount: String(kit.discountAmount ?? 0),
    freightClass: kit.freightClass ?? "",
    active: kit.active,
    members,
    rules,
  };
};

/** Where a member added in the editor goes: after every member already on the kit. */
export const getNextMemberSortOrder = (members: WsmKitMemberDraft[]) =>
  members.reduce((max, member) => Math.max(max, member.sortOrder), -1) + 1;

export const getKitMemberInputs = (members: WsmKitMemberDraft[]): WsmKitMemberInput[] =>
  members.map(member => ({
    ...(member.id ? { id: member.id } : { key: member.key }),
    variant: member.variantId,
    quantity: member.quantity,
    // Sent as stored: the editor has no reorder, so row position is not merchant
    // intent, and renumbering it would erase an imported 0, 10, 20 spacing.
    sortOrder: member.sortOrder,
    // A blank label is a clear, sent as null so the storefront shows the product name.
    ...(member.labelTouched ? { label: member.label.trim() || null } : {}),
  }));

/**
 * Existing members are named by ID, members created in this same payload by
 * their key, which is the split the contract's `targetIds` / `targetKeys` pair
 * exists for. A rule whose subject row is gone is dropped, not sent.
 */
export const getKitRuleInputs = (
  rules: WsmKitRuleDraft[],
  members: WsmKitMemberDraft[],
): WsmKitMemberRuleInput[] => {
  const byKey = new Map(members.map(member => [member.key, member]));

  return rules
    .filter(rule => byKey.has(rule.subjectKey))
    .map(rule => {
      const subject = byKey.get(rule.subjectKey) as WsmKitMemberDraft;
      const targets = rule.targetKeys
        .map(key => byKey.get(key))
        .filter((member): member is WsmKitMemberDraft => !!member);
      const targetIds = targets.filter(member => !!member.id).map(member => member.id as string);
      const targetKeys = targets.filter(member => !member.id).map(member => member.key);

      return {
        ...(rule.id ? { id: rule.id } : {}),
        ...(subject.id ? { subjectId: subject.id } : { subjectKey: subject.key }),
        kind: rule.kind,
        ...(targetIds.length ? { targetIds } : {}),
        ...(targetKeys.length ? { targetKeys } : {}),
        message: rule.message,
      };
    });
};

/**
 * The rules target picker, scoped to this kit's own members and never to the
 * subject itself. The server enforces the same thing with RULE_TARGET_NOT_IN_KIT;
 * this is what stops a merchant reaching the refusal in the first place.
 */
export const getRuleTargetChoices = (members: WsmKitMemberDraft[], subjectKey: string) =>
  members
    .filter(member => member.key !== subjectKey)
    .map(member => ({
      value: member.key,
      label: member.sku ? `${member.productName} (${member.sku})` : member.productName,
    }));

/**
 * The one place a kit's discount decides what SHAPE it is. Everything downstream
 * renders it through WsmAmount, so a percentage and a fixed amount can never sit
 * in one column as "10.00%" and "0.00 USD" again.
 */
export const getKitDiscountAmountProps = (
  discountKind: WsmKitDiscountKind,
  discountAmount: unknown,
  currencyCode?: string | null,
): { amount: number; currency: string | null; basis: WsmAmountBasis } => {
  const amount = Number(discountAmount ?? 0);

  return discountKind === WsmKitDiscountKind.PERCENT
    ? { amount, currency: null, basis: "percent" }
    : { amount, currency: currencyCode ?? null, basis: "fixed" };
};

/** Two members is the floor for a rule: one member has nothing to depend on. */
export const kitCanHaveRules = (memberCount: number) => memberCount >= 2;

/**
 * The whole payload one save sends. Members and rules ride together because a
 * rule written in this same submit points at a member by its `key`, and both
 * lists are REPLACE semantics: what is on screen is what the kit ends up with.
 */
export const getKitUpdateInput = (data: WsmKitFormData): WsmKitConfigUpdateInput => ({
  discountKind: data.discountKind,
  discountAmount: data.discountAmount.trim() || "0",
  freightClass: data.freightClass,
  active: data.active,
  members: getKitMemberInputs(data.members),
  rules: getKitRuleInputs(data.rules, data.members),
});

export const getKitCreateInput = (data: WsmKitCreateFormData): WsmKitConfigCreateInput => ({
  ...getKitUpdateInput(data),
  collection: data.collection,
});
